// TrainingClientsListView
//
// Sidebar entry point for the "Synthetic Clients in the Therapist UI" feature.
// Lists the therapist's active synthetic clients (separate from the real
// clinical caseload so the two can never be commingled) and provides a
// "Start new practice" picker that surfaces the therapist's authorised
// platform scenarios.
//
// On start, the backend materialises a synthetic User + linked Conversation
// and the view navigates into the full ClientDetailView for that user.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import api from '../../../services/api';
import therapistStyles from '../styles/therapistStyles';
import { tokens } from '../styles/tokens';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_LABEL = {
  active: 'Active',
  in_progress: 'Active',
  paused: 'Paused',
  ended: 'Ended',
  completed: 'Ended',
  abandoned: 'Ended',
};

const STATUS_CHIP_PROPS = {
  active:      { sx: { bgcolor: tokens.color.status.routine.bg,  color: tokens.color.status.routine.fg } },
  in_progress: { sx: { bgcolor: tokens.color.status.routine.bg,  color: tokens.color.status.routine.fg } },
  paused:      { sx: { bgcolor: tokens.color.status.moderate.bg, color: tokens.color.status.moderate.fg } },
  ended:       { sx: { bgcolor: tokens.color.status.neutral.bg,  color: tokens.color.status.neutral.fg } },
  completed:   { sx: { bgcolor: tokens.color.status.neutral.bg,  color: tokens.color.status.neutral.fg } },
  abandoned:   { sx: { bgcolor: tokens.color.status.neutral.bg,  color: tokens.color.status.neutral.fg } },
};

const formatRelativeTime = (value) => {
  if (!value) return 'Never';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'Never';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return new Date(value).toLocaleString();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString();
};

// Normalise the various shapes a backend might return for a training-client
// row. The spec doesn't lock the route's response yet — the helper defends
// against the most likely permutations so the UI does not crash if the
// backend ships a slightly different shape.
// TrainingSession statuses that mean the practice is over. Mirrors
// TERMINAL_TRAINING_STATUSES in TherapistClientController: Play refuses these
// with a 409, so the list must not show them as running either.
const TERMINAL_SESSION_STATUSES = new Set([
  'completed', 'abandoned', 'timed_out', 'awaiting_formulation', 'awaiting_review', 'reviewed',
]);

const normaliseTrainingClient = (row) => {
  if (!row || typeof row !== 'object') return null;

  const id = row._id || row.id || row.userId || row.syntheticUserId || null;
  if (!id) return null;

  const profile = row.syntheticProfile || row.profile || {};
  const session =
    row.trainingSession || row.session || row.originatingTrainingSession || {};
  const scenario =
    row.scenario || profile.scenario || session.scenario || {};
  const persona =
    row.persona ||
    row.clientPersona ||
    profile.clientPersona ||
    profile.persona ||
    {};

  // ST-20260922-0003: every row used to render "Active", including practices
  // that had finished hours earlier. The server sends the real state as
  // `trainingSessionStatus` / `trainingSessionPaused` (see getMyTrainingClients),
  // but this helper only looked for `row.status` and a nested `session` object,
  // neither of which the payload contains — so every branch missed and the
  // final fallback hardcoded 'active'. Read the server's values first, and
  // collapse every terminal status onto 'ended' so an unmapped one (timed_out,
  // awaiting_review, …) can never read as running.
  const sessionStatus =
    row.trainingSessionStatus || session.status || session.state || null;
  const status = sessionStatus
    ? (TERMINAL_SESSION_STATUSES.has(sessionStatus)
        ? 'ended'
        : row.trainingSessionPaused
          ? 'paused'
          : sessionStatus)
    : (row.status ||
      (row.endedAt ? 'ended' : row.pausedAt ? 'paused' : 'active'));

  const lastActivity =
    row.lastActivity ||
    row.lastActivityAt ||
    row.updatedAt ||
    session.lastActivityAt ||
    session.updatedAt ||
    null;

  return {
    id,
    name: row.name || persona.displayName || persona.name || 'Synthetic client',
    email: row.email || null,
    scenarioName: scenario.name || scenario.title || row.scenarioName || 'Practice scenario',
    personaName: persona.displayName || persona.name || row.personaName || row.name || 'Synthetic persona',
    status,
    lastActivity,
    trainingSessionId: session._id || session.id || row.trainingSessionId || profile.originatingTrainingSessionId || null,
    raw: row,
  };
};

const extractClientList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.clients)) return payload.clients;
  if (Array.isArray(payload.trainingClients)) return payload.trainingClients;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.clients)) return payload.data.clients;
  return [];
};

const extractScenarioList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.scenarios)) return payload.scenarios;
  if (payload.scenarios && Array.isArray(payload.scenarios.list)) return payload.scenarios.list;
  if (payload.data && Array.isArray(payload.data.scenarios?.list)) return payload.data.scenarios.list;
  if (payload.data && Array.isArray(payload.data.scenarios)) return payload.data.scenarios;
  return [];
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TrainingClientsListView({ token, onClientSelect }) {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState('');
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [starting, setStarting] = useState(null); // scenarioId currently starting

  // Per-row "ending" lock so the End button only spins for the targeted row.
  const [endingId, setEndingId] = useState(null);

  // Delete confirmation + per-row "deleting" lock.
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, label } | null
  const [deletingId, setDeletingId] = useState(null);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchClients = useCallback(async () => {
    if (!token) {
      setError('Authentication token not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await api.getMyTrainingClients(token);
      const rows = extractClientList(payload).map(normaliseTrainingClient).filter(Boolean);
      setClients(rows);
    } catch (err) {
      console.error('Failed to load training clients:', err);
      setError(err?.message || 'Failed to load training clients.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const fetchScenarios = useCallback(async () => {
    if (!token) return;
    setScenariosLoading(true);
    setScenariosError('');
    try {
      const payload = await api.getAvailableTherapistScenarios(token);
      setScenarios(extractScenarioList(payload));
    } catch (err) {
      console.error('Failed to load practice scenarios:', err);
      setScenariosError(err?.message || 'Could not load practice scenarios.');
      setScenarios([]);
    } finally {
      setScenariosLoading(false);
    }
  }, [token]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const handleOpenPicker = () => {
    setPickerOpen(true);
    if (scenarios.length === 0 && !scenariosLoading) {
      fetchScenarios();
    }
  };

  const handleClosePicker = () => {
    if (starting) return; // don't allow closing mid-start
    setPickerOpen(false);
    setScenarioFilter('');
  };

  const handleStartScenario = async (scenario) => {
    const scenarioId = scenario?._id || scenario?.id;
    if (!scenarioId) {
      setToast('That scenario is missing an id — cannot start.');
      return;
    }
    setStarting(scenarioId);
    try {
      const payload = await api.startTherapistPracticeSession(
        { scenarioId, materialiseAsClient: true },
        token
      );

      // Backend response per
      // server/controllers/training/trainingSessionController.js:
      //   { success, data: { session, scenarioBrief, syntheticUser, conversation, route } }
      const data = payload?.data || payload || {};
      const syntheticUserId =
        data.syntheticUser?._id ||
        data.syntheticUser?.id ||
        data.syntheticUserId ||
        data.clientId ||
        data.userId ||
        data.session?.syntheticUserId ||
        data.client?._id ||
        null;
      const route =
        data.route ||
        data.redirectTo ||
        (syntheticUserId
          ? `/therapist/dashboard?tab=training-clients&clientId=${syntheticUserId}`
          : null);

      setPickerOpen(false);
      setScenarioFilter('');

      if (route) {
        navigate(route);
      } else {
        setToast('Session started but the response did not include a synthetic client id — refresh the list.');
        fetchClients();
      }
    } catch (err) {
      console.error('Failed to start practice session:', err);
      setToast(err?.message || 'Could not start practice session.');
    } finally {
      setStarting(null);
    }
  };

  const handleOpenClient = (row) => {
    if (!row?.id) return;
    if (typeof onClientSelect === 'function') {
      onClientSelect({
        _id: row.id,
        id: row.id,
        name: row.name,
        email: row.email || undefined,
        kind: 'synthetic',
        syntheticProfile: row.raw?.syntheticProfile || undefined,
        trainingSession: row.raw?.trainingSession || row.raw?.session || undefined,
        scenario: row.raw?.scenario || undefined,
      });
    } else {
      navigate(`/therapist/dashboard?tab=training-clients&clientId=${row.id}`);
    }
  };

  const handleEndPractice = async (row) => {
    if (!row?.trainingSessionId) {
      setToast('No training session id is linked to this client.');
      return;
    }
    setEndingId(row.id);
    try {
      await api.endTrainingSession(row.trainingSessionId, token);
      setToast(`Ended practice session for ${row.personaName}.`);
      await fetchClients();
    } catch (err) {
      console.error('Failed to end practice session:', err);
      setToast(err?.message || 'Could not end practice session.');
    } finally {
      setEndingId(null);
    }
  };

  // Open the confirmation dialog. The actual delete fires from
  // handleConfirmDelete only after the therapist confirms — these are
  // training artefacts, not real clinical data, but a confirmation step is
  // still the right UX so a misclick on a long list doesn't lose work.
  const handleRequestDelete = (row) => {
    setDeleteTarget({
      id: row.id,
      label: row.personaName || row.scenarioTitle || 'this practice session',
      scenarioTitle: row.scenarioTitle,
    });
  };

  const handleCancelDelete = () => setDeleteTarget(null);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    const targetLabel = deleteTarget.label;
    setDeletingId(targetId);
    try {
      await api.deleteTrainingClient(targetId, token);
      setToast(`Deleted "${targetLabel}".`);
      setDeleteTarget(null);
      await fetchClients();
    } catch (err) {
      console.error('Failed to delete training client:', err);
      setToast(err?.message || 'Could not delete training client.');
    } finally {
      setDeletingId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Derived UI bits
  // -------------------------------------------------------------------------

  const filteredScenarios = useMemo(() => {
    const term = scenarioFilter.trim().toLowerCase();
    if (!term) return scenarios;
    return scenarios.filter((s) => {
      const hay = [
        s.name,
        s.title,
        s.category,
        s.difficulty,
        s.description,
        s.scenarioContext?.presentingComplaint,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [scenarios, scenarioFilter]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box>
      {/* Header band — uses the existing warning palette per spec
          (banner/chips deliberately reuse status.high; the magenta
          training token family is deferred to v2). */}
      <Paper
        elevation={0}
        sx={{
          ...therapistStyles.contentCard,
          mb: 3,
          p: 2.5,
          borderLeft: `4px solid ${tokens.color.status.high.fg}`,
          backgroundColor: tokens.color.status.high.bg,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SmartToyIcon sx={{ color: tokens.color.status.high.fg }} fontSize="small" />
              <Typography variant="h6" sx={{ color: tokens.color.status.high.fg, fontWeight: 700 }}>
                Training Clients
              </Typography>
              <Chip
                size="small"
                label="Synthetic · training only"
                sx={{
                  bgcolor: tokens.color.status.high.fg,
                  color: tokens.color.status.high.bg,
                  fontWeight: 600,
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.status.high.fg }}>
              Practice scenarios run against synthetic clients. They never appear in your real caseload and do not trigger
              clinical alerts, supervisor escalations, or exportable reports.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleOpenPicker}
            sx={{
              bgcolor: tokens.color.status.high.fg,
              color: tokens.color.status.high.bg,
              fontWeight: 600,
              '&:hover': { bgcolor: tokens.color.status.high.fg, opacity: 0.9 },
            }}
          >
            Start new practice
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...therapistStyles.contentCard, p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : clients.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              You don&apos;t have any active practice clients yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click <strong>Start new practice</strong> to spin up a synthetic client from an authorised scenario.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {clients.map((row, idx) => {
              const statusKey = String(row.status || '').toLowerCase();
              const chipProps = STATUS_CHIP_PROPS[statusKey] || STATUS_CHIP_PROPS.active;
              const statusLabel = STATUS_LABEL[statusKey] || (row.status || 'Active');
              const isEnding = endingId === row.id;
              const isEnded = ['ended', 'completed', 'abandoned'].includes(statusKey);
              const isDeleting = deletingId === row.id;

              return (
                <React.Fragment key={row.id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenClient(row)}
                        >
                          Open
                        </Button>
                        <Tooltip
                          title={
                            isEnded
                              ? 'This practice session has already ended.'
                              : !row.trainingSessionId
                              ? 'Missing training session id.'
                              : 'End practice session'
                          }
                        >
                          <span>
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              startIcon={isEnding ? <CircularProgress size={14} /> : <StopCircleIcon fontSize="small" />}
                              onClick={() => handleEndPractice(row)}
                              disabled={isEnded || isEnding || !row.trainingSessionId}
                            >
                              {isEnding ? 'Ending…' : 'End practice'}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete this practice client and its conversation, goals, journal, assessments and activity. Synthetic data only — cannot be undone.">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRequestDelete(row)}
                              disabled={isDeleting}
                              aria-label={`Delete training client ${row.personaName || ''}`}
                            >
                              {isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemButton onClick={() => handleOpenClient(row)} sx={{ py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: tokens.color.status.high.bg, color: tokens.color.status.high.fg }}>
                          {(row.personaName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {row.personaName}
                            </Typography>
                            <Chip
                              size="small"
                              label="Training"
                              aria-label="Synthetic training client"
                              sx={{
                                bgcolor: tokens.color.status.high.bg,
                                color: tokens.color.status.high.fg,
                                fontWeight: 600,
                              }}
                            />
                            <Chip size="small" label={statusLabel} {...chipProps} />
                          </Stack>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                            <Typography component="span" variant="body2" color="text.secondary">
                              Scenario: {row.scenarioName}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Last activity: {formatRelativeTime(row.lastActivity)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {idx < clients.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Scenario picker dialog */}
      <Dialog
        open={pickerOpen}
        onClose={handleClosePicker}
        fullWidth
        maxWidth="sm"
        aria-labelledby="start-practice-dialog-title"
      >
        <DialogTitle id="start-practice-dialog-title" sx={{ pr: 6 }}>
          Start a new practice
          <IconButton
            aria-label="Close"
            onClick={handleClosePicker}
            disabled={!!starting}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search scenarios…"
            value={scenarioFilter}
            onChange={(e) => setScenarioFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              ),
            }}
            sx={{ mb: 2 }}
          />

          {scenariosError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScenariosError('')}>
              {scenariosError}
            </Alert>
          )}

          {scenariosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredScenarios.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {scenarios.length === 0
                  ? 'No scenarios available. Ask your clinical supervisor or admin to grant scenarios on your profile.'
                  : 'No scenarios match your search.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {filteredScenarios.map((s) => {
                const id = String(s._id || s.id);
                const isStarting = starting === id;
                const presenting =
                  s.scenarioContext?.presentingComplaint || s.description || '';
                return (
                  <Card key={id} variant="outlined">
                    <CardContent sx={{ pb: '12px !important' }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {s.name || s.title || 'Unnamed scenario'}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                            {s.difficulty && (
                              <Chip size="small" variant="outlined" label={s.difficulty} sx={{ textTransform: 'capitalize' }} />
                            )}
                            {s.category && (
                              <Chip size="small" variant="outlined" label={String(s.category).replace(/_/g, ' ')} />
                            )}
                          </Stack>
                          {presenting && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {presenting}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleStartScenario(s)}
                          disabled={!!starting}
                          startIcon={isStarting ? <CircularProgress size={14} /> : <PlayArrowIcon fontSize="small" />}
                        >
                          {isStarting ? 'Starting…' : 'Start'}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePicker} disabled={!!starting}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete-training-client confirmation dialog. Synthetic data only, but
          a confirmation step is still the right UX so a misclick doesn't
          irreversibly bin a long practice session in one tap. */}
      <Dialog open={!!deleteTarget} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete training client?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will permanently delete <strong>{deleteTarget?.label}</strong>
            {deleteTarget?.scenarioTitle ? <> (scenario &ldquo;{deleteTarget.scenarioTitle}&rdquo;)</> : null}
            {' '}and everything tied to it: the conversation, any goals,
            journal entries, care notes, formulations, activity events,
            alerts and the originating training session.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Synthetic training data only — cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={!!deletingId}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={!!deletingId}
            startIcon={deletingId ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
          >
            {deletingId ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}

export default TrainingClientsListView;
